// RESONATE_ANALYTICS_INSTRUMENTED + RESONATE_PERF_V2 (mobile <1s target)
// Above-fold (Nav, Hero, Marquee) is eager. Everything below is lazy-loaded
// via IntersectionObserver so the initial paint is dominated by the hero only.
import { lazy, Suspense, useEffect } from "react";
import { Nav } from "@/components/site/Nav";
import { Hero } from "@/components/site/Hero";
import { HeroShowcase } from "@/components/site/HeroShowcase";
import { Marquee } from "@/components/site/Marquee";
import { Footer } from "@/components/site/Footer";
import { StickyBar } from "@/components/site/StickyBar";
import { MobileStickyCta } from "@/components/site/MobileStickyCta";
import { TrackedSection } from "@/components/TrackedSection";
import { LazyMount } from "@/components/LazyMount";
import { SEO } from "@/components/SEO";

const HowItWorks = lazy(() => import("@/components/site/HowItWorks").then(m => ({ default: m.HowItWorks })));
const Features = lazy(() => import("@/components/site/Features").then(m => ({ default: m.Features })));
const Compare = lazy(() => import("@/components/site/Compare").then(m => ({ default: m.Compare })));
const Pricing = lazy(() => import("@/components/site/Pricing").then(m => ({ default: m.Pricing })));
const FAQ = lazy(() => import("@/components/site/FAQ").then(m => ({ default: m.FAQ })));
const FinalCTA = lazy(() => import("@/components/site/FinalCTA").then(m => ({ default: m.FinalCTA })));

const SectionSkeleton = () => <div aria-hidden style={{ minHeight: 320 }} />;

export default function Landing() {
  // The static hero shell (index.html) is an overlay covering the viewport
  // until React has painted the real hero underneath it. Remove it once
  // mounted — see RESONATE_HERO_SHELL_V1 in index.html for why it exists.
  useEffect(() => {
    document.getElementById("hero-shell")?.remove();
  }, []);

  return (
    <div data-testid="landing-root" className="min-h-screen bg-[#FAFAFF] text-[#111827] antialiased">
      <SEO
        title="Resonate - LinkedIn AI That Sounds Like You"
        description="Resonate turns your real work in Notion, Slack, GitHub and HubSpot into LinkedIn posts that sound exactly like you. Predict engagement before you publish. From $29/mo."
        url="https://www.getresonate.ai/"
        image="https://www.getresonate.ai/og-image.png"
        keywords={["LinkedIn AI", "LinkedIn content generator", "AI LinkedIn posts", "personal brand", "thought leadership"]}
      />
      <Nav />
      <main>
        <TrackedSection id="hero" name="Hero"><Hero /></TrackedSection>
        <TrackedSection id="hero-showcase" name="Hero Showcase"><HeroShowcase /></TrackedSection>
        <TrackedSection id="marquee" name="Marquee"><Marquee /></TrackedSection>

        {/* Everything below is deferred until it scrolls near the viewport.
            Problem section removed per user directive ("why resonate" cut). */}
        <Suspense fallback={<SectionSkeleton />}>
          <LazyMount rootMargin="600px"><TrackedSection id="how-it-works" name="How It Works"><HowItWorks /></TrackedSection></LazyMount>
          <LazyMount rootMargin="600px"><TrackedSection id="features" name="Features"><Features /></TrackedSection></LazyMount>
          <LazyMount rootMargin="600px"><TrackedSection id="compare" name="Compare"><Compare /></TrackedSection></LazyMount>
          <LazyMount rootMargin="600px"><TrackedSection id="pricing" name="Pricing"><Pricing /></TrackedSection></LazyMount>
          <LazyMount rootMargin="600px"><TrackedSection id="faq" name="FAQ"><FAQ /></TrackedSection></LazyMount>
          <LazyMount rootMargin="600px"><TrackedSection id="final-cta" name="Final CTA"><FinalCTA /></TrackedSection></LazyMount>
        </Suspense>
      </main>
      <Footer />
      <StickyBar />
      <MobileStickyCta />
    </div>
  );
}
